import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, Landmark, Star, Users } from "lucide-react";
import { LeadForm } from "./LeadForm";
import { BRAND } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1534818113099-dbe2b2e800ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxoaXNwYW5pYyUyMGZhbWlseSUyMGZpbmFuY2lhbCUyMHBsYW5uaW5nJTIwcmV0aXJlbWVudCUyMHNtaWxlfGVufDB8fHx8MTc5MDY1MjMxOXww&ixlib=rb-4.1.0&q=85";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const bullets = [
  { icon: ShieldCheck, text: "Protección de vida para tu familia" },
  { icon: TrendingUp, text: "Crece con el mercado, sin perder cuando baja" },
  { icon: Landmark, text: "Dinero accesible libre de impuestos" },
];

export const Hero = ({ onSuccess }) => (
  <section id="inicio" className="hero-bg relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 grid-noise pointer-events-none" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3.5 py-1.5 shadow-sm">
          <span className="relative flex h-2.5 w-2.5">
            <span className="pulse-ring absolute inset-0 rounded-full" />
            <span className="relative rounded-full h-2.5 w-2.5 bg-growth" />
          </span>
          <span className="text-xs font-semibold text-slate-700">Cotización gratuita · Asesoría en español</span>
        </motion.div>

        <motion.h1
          {...fade(0.1)}
          className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-navy"
          data-testid="hero-headline"
        >
          Protege a tu familia y{" "}
          <span className="gradient-text">haz crecer tu dinero</span> sin riesgo de mercado.
        </motion.h1>

        <motion.p {...fade(0.2)} className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
          Un seguro de vida IUL (Indexed Universal Life) une la tranquilidad de una protección
          completa con un crecimiento ligado al índice S&amp;P 500 — con piso garantizado del 0%
          y acceso a tu efectivo libre de impuestos.
        </motion.p>

        <motion.ul {...fade(0.3)} className="mt-8 space-y-3">
          {bullets.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-slate-800 font-medium">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-white shadow-sm ring-1 ring-slate-200 text-growth">
                <Icon className="h-5 w-5" />
              </span>
              {text}
            </li>
          ))}
        </motion.ul>

        <motion.div {...fade(0.4)} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-3">
              {[HERO_IMG, "https://images.unsplash.com/photo-1626984367254-fe8fa5e9c552?crop=entropy&cs=srgb&fm=jpg&w=120&q=70", "https://images.unsplash.com/photo-1767159695965-a4c2db6dec3d?crop=entropy&cs=srgb&fm=jpg&w=120&q=70"].map((src, i) => (
                <img key={i} src={src} alt="" className="h-10 w-10 rounded-full object-cover ring-2 ring-white" />
              ))}
            </div>
            <div>
              <div className="flex text-amber-400">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-xs text-slate-600 font-medium">Familias hispanas ya protegidas</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users className="h-4 w-4 text-brand-blue" />
            <span><span className="font-bold text-navy">+1,400</span> personas confían en {BRAND.name}</span>
          </div>
        </motion.div>
      </div>

      <motion.div
        id="formulario"
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-5 relative scroll-mt-24"
      >
        <div className="absolute -inset-2 rounded-[2rem] gradient-brand opacity-20 blur-2xl" />
        <div className="relative rounded-3xl bg-white shadow-2xl shadow-navy/10 border border-slate-200 p-6 sm:p-8" data-testid="hero-form-card">
          <div className="flex items-center gap-3 mb-6">
            <img src={BRAND.logo} alt="" className="h-12 w-12 rounded-xl object-cover shadow-md ring-1 ring-slate-200" />
            <div>
              <h2 className="font-heading font-extrabold text-navy text-base md:text-lg leading-tight">
                Consigue tu cotización gratis de IUL
              </h2>
              <p className="text-xs text-slate-500 font-medium">Solo 2 datos · Te contactamos hoy</p>
            </div>
          </div>
          <LeadForm onSuccess={onSuccess} />
        </div>
        <div className="absolute -top-5 -right-3 sm:-right-6 animate-float hidden sm:block">
          <div className="rounded-2xl bg-navy text-white px-4 py-3 shadow-xl flex items-center gap-3 rotate-2">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-growth text-white font-extrabold text-sm">$50</span>
            <div className="leading-tight">
              <p className="text-xs text-slate-300">Bono al completar</p>
              <p className="text-sm font-bold">Descuento en seguro de auto</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);
